import React, { PureComponent } from 'react';

import { Icon } from '@gumtree/ui-library';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import slug from '@gumtree/ui-library/src/utils/slug';
import { trackGA4PreNav } from '@gumtree/shared/src/util/ga4-shared';
import { getDomainFromUrl } from '@gumtree/shared/src/util/url-service';

import './side-menu-item.scss';
import { MenuItemLabel } from './side-menu-data';
import type { OpenDialog } from '../../use-modal';
import { LoginModalType } from '../../reducers/common/login-modal';

export default class SideMenuItem extends PureComponent<
    Props & typeof SideMenuItem['defaultProps']
> {
    static defaultProps = {
        betaLabel: false,
        className: '',
        eventAction: '',
        eventLabel: '',
        features: {},
        icon: '',
        id: '',
        isBannerTakeoverShowing: false,
        label: '',
        launchBanner: () => {},
        link: '',
        onClick: () => {},
        qaAttr: undefined,
        pageType: '',
        showUnreadBadge: false,
        unreadMessagesCount: 0,
    };

    handleClick = async (
        event: React.MouseEvent<HTMLAnchorElement>,
        isBannerTakeoverShowing,
        hasFeatureToggle
    ) => {
        const {
            eventAction,
            eventLabel,
            link,
            onClick,
            trackAndNavigate,
            launchBanner,
            label,
            pageType,
        } = this.props;

        onClick(event);

        trackGA4PreNav<GA4.ClickPrimaryNavigation>({
            event: 'click_primary_navigation',
            linkDomain: getDomainFromUrl(link),
            linkText: label,
            linkURL: link,
            navigationCategory: 'menu',
            navigationHeader: undefined,
            navigationHierarchy: 'sub header',
            pageClickedFrom: pageType,
        });

        if (eventAction) {
            event.preventDefault();
            trackAndNavigate(eventAction, link, eventLabel);
        }

        if (isBannerTakeoverShowing && hasFeatureToggle) {
            event.preventDefault();
            launchBanner(event.currentTarget.href);
        }
    };

    render() {
        const {
            icon,
            label,
            link,
            className,
            isBannerTakeoverShowing,
            features,
            betaLabel,
            openDialog,
            showUnreadBadge,
            unreadMessagesCount,
        } = this.props;
        const hasFeatureToggle =
            features[0] && features[0].length && features[0].includes('POST_AD_APP_SIGN_POST');
        const shouldShowUnreadBadge = showUnreadBadge && unreadMessagesCount > 0;
        const unreadMessagesCountToDisplay = Math.min(unreadMessagesCount, 99);

        return (
            <a
                className={`side-menu-link ${className}`}
                href={link}
                onClick={(event) => {
                    this.handleClick(event, isBannerTakeoverShowing, hasFeatureToggle);
                    label === MenuItemLabel.Login && openDialog?.(event, LoginModalType.LOGIN, true);
                    label === MenuItemLabel.SignUp && openDialog?.(event, LoginModalType.REGISTER, false);
                }}
                {...qaAttribute(slug(`side-menu-link-${label}`))}
            >
                {icon && <Icon type={icon} />}
                {(label || shouldShowUnreadBadge) && (
                    <span className="side-menu-label-group">
                        {label}
                        {shouldShowUnreadBadge && (
                            <span className="side-menu-unread-badge" data-testid="side-menu-unread-badge">
                                {unreadMessagesCountToDisplay}
                            </span>
                        )}
                    </span>
                )}
                {betaLabel && <span className="has-beta-label">Beta</span>}
            </a>
        );
    }
}

interface Props {
    betaLabel?: boolean;
    className?: string;
    eventAction?: string;
    eventLabel?: string;
    features: any;
    icon?: string;
    id?: string;
    isBannerTakeoverShowing?: boolean;
    label?: string;
    launchBanner?(href: string): void;
    link?: string;
    openDialog?: OpenDialog;
    onClick?(event: React.MouseEvent): void;
    pageType?: string;
    qaAttr?: string;
    showUnreadBadge?: boolean;
    trackAndNavigate(...args: any[]): void;
    unreadMessagesCount?: number;
}
